import { fetchMembershipExcel } from '@/api/export';
import { jobsQueryOptions } from '@/api/jobsQueryOptions';
import { listHeadersQueryOptions } from '@/api/listQueryOptions';
import { useFeeClearedTroopMutation } from '@/api/mem/memberMembershipQueryOptions';
import { fetchMembershipEmails, FilterParam, MembershipType, SortParam, updateMembershipTable, VisibilityParam } from '@/api/memberships';
import { membershipsQueryOptions } from '@/api/membershipsQueryOptions';
import { OrganizationLevelType } from '@/api/organizations';
import { organizationDynamicFieldsQueryOptions, organizationsAvailableQueryOptions } from '@/api/organizationsQueryOptions';
import { fetchDetailPermissionsQueryOptions } from '@/api/permissionsQueryOptions';
import { sectionsAvailableQueryOptions } from '@/api/sectionsQueryOptions';
import { queryClient } from '@/App';
import { ApiErrorComponent } from '@/components/ApiErrorComponent';
import { useColumnState } from '@/components/hooks/useColumnState';
import useGridPageSize from '@/components/hooks/useGridPageSize';
import { copyFilterModelToParams, usePersistedFilterModel } from '@/components/hooks/usePersistedFilterModel';
import { usePersistedPinnedColumns } from '@/components/hooks/usePersistedPinnedColumns';
import { copySortModelToParams, usePersistedSortModel } from '@/components/hooks/usePersistedSortModel';
import { copyVisibilityModelToParams, usePersistedVisibilityModel } from '@/components/hooks/usePersistedVisibilityModel';
import useResponsive from '@/components/hooks/useResponsive';
import LoadProgress from '@/components/LoadProgress';
import { ScoregQuickFilter } from '@/components/mui-x/datagrid/filter/ScoregQuickFilter';
import { GridToolbarEmailContainer } from '@/components/mui-x/toolbar/GridToolbarEmailContainer';
import { GridToolbarExcelExportContainer } from '@/components/mui-x/toolbar/GridToolbarExcelExportContainer';
import GridToolbarPaginationButton from '@/components/mui-x/toolbar/GridToolbarPaginationButton';
import { getOrganization } from '@/store/auth/authSlice';
import getColumns from '@/ui/memberships/columns';
import MembershipsSidebar from '@/ui/memberships/MembershipsSidebar';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Box, ButtonProps, FormControlLabel, IconButton, Menu, MenuItem, styled, Switch, Tooltip } from '@mui/material';
import { GridCellModes, GridCellModesModel, GridCellParams, GridColDef, GridColumnVisibilityModel, GridExportMenuItemProps, GridFilterItem, GridFilterModel, GridLogicOperator, GridPagination, GridPaginationModel, GridPinnedColumnFields, GridSortModel, GridToolbarColumnsButton, GridToolbarFilterButton, gridVisibleColumnDefinitionsSelector, Toolbar } from '@mui/x-data-grid';
import { DataGridPremium, useGridApiRef } from '@mui/x-data-grid-premium';
import { deDE } from '@mui/x-data-grid/locales';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Buffer } from 'buffer';
import { t } from 'i18next';
import { enqueueSnackbar } from 'notistack';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

type MembershipsSearch = {
    filter: FilterParam[],
    sort: SortParam[],
    visibility: VisibilityParam[] | undefined,
}

export const Route = createFileRoute('/_auth/memberships')({
    validateSearch: (search: Record<string, unknown>): MembershipsSearch => {
        return {
            filter: search.filter as FilterParam[] || [],
            visibility: search.visibility as VisibilityParam[] || [],
            sort: search.sort as SortParam[] || [],
        }
    },
    component: MembershipsComponent,
    errorComponent: ({ error }) => {
        if (error) {
            return <ApiErrorComponent error={error as Error} />
        } else {
            return <ApiErrorComponent />
        }
    },
})

function MembershipsComponent() {
    const { filter, sort, visibility } = Route.useSearch()

    const gridPageSize = useGridPageSize()
    const pageSizeOptions = gridPageSize.getPageSizeOptions('memberships', 40)

    const [paginationModel, setPaginationModel] = React.useState(gridPageSize.getPaginationModel('memberships', 40));

    const responsive = useResponsive()
    const sessionOrganization = useSelector(getOrganization)

    const [editable, setEditable] = React.useState(false)

    const apiRef = useGridApiRef()

    const initialFilterModel: GridFilterModel = {
        items: filter.map((filter) => {
            if ((filter.field.startsWith('properties.joblist')
                || filter.field.startsWith('properties.section')
                || filter.field.startsWith('scoutstate')) && filter.value instanceof String) {
                return {
                    id: filter.field,
                    field: filter.field,
                    operator: filter.operator,
                    value: filter.value.split(',')
                } as GridFilterItem
            }
            return {
                id: filter.field,
                field: filter.field,
                operator: filter.operator,
                value: filter.value
            } as GridFilterItem
        }),
        logicOperator: GridLogicOperator.And,
    }
    // console.log(filter)
    // console.log(initialFilterModel)

    const [filterModel, setFilterModel] = usePersistedFilterModel('membership', initialFilterModel)

    // console.log(filterModel)
    const [filterParams, setFilterParams] = React.useState(copyFilterModelToParams(filterModel))

    const initialSortModel = sort.map((sort) => {
        return {
            field: sort.field,
            sort: sort.direction
        }
    }) as GridSortModel;

    const [sortModel, setSortModel] = usePersistedSortModel('membership', initialSortModel)
    const [sortParams, setSortParams] = React.useState(copySortModelToParams(sortModel))

    // if (settingTableColumnUuid) {
    //     const settingTableColumnQuery = useSuspenseQuery(fetchSettingTableColumnQueryOptions(settingTableColumnUuid));
    //     const settingTableColumn = settingTableColumnQuery.data;

    //     if (settingTableColumnQuery.isLoading || settingTableColumnQuery.isFetching) { 
    //         return <LoadProgress/>
    //     }
    //     settingTableColumn
    // }
    let initialVisibilityModel = {} as GridColumnVisibilityModel;
    if (visibility) {
        initialVisibilityModel = {
            'properties.scoutstate': false,
            'properties.joblist': false,
            'properties.section': false,
            'properties.paymentTroop': false,
            'properties.paymentDivision': false,
            'properties.paymentFederation': false,
            'properties.registrationStatus': false,
            'properties.registrationOrganization': false,
            'member.street': false,
            'member.postcode': false,
            'member.city': false,
            'member.addressaddition1': false,
            'member.addressaddition2': false,
            'organization.name': sessionOrganization.organizationLevel != OrganizationLevelType.TROOP,
            'contacts.contact1Type': false,
            'contacts.contact1Name': false,
            'contacts.contact1Email': false,
            'contacts.contact1Phone': false,
            'contacts.contact1Address': false,
            'contacts.contact1Profession': false,
            'contacts.contact2Type': false,
            'contacts.contact2Name': false,
            'contacts.contact2Email': false,
            'contacts.contact2Phone': false,
            'contacts.contact2Address': false,
            'contacts.contact2Profession': false,
            'dynamicFields.dynamicField1': false,
            'dynamicFields.dynamicField2': false,
            'dynamicFields.dynamicField3': false,
            'dynamicFields.dynamicField4': false,
            'dynamicFields.dynamicField5': false,
            'dynamicFields.checkField1': false,
            'dynamicFields.checkField2': false,
            'dynamicFields.checkField3': false,
            'dynamicFields.checkField4': false,
            'dynamicFields.checkField5': false,
            'dynamicFields.dateField1': false,
            'dynamicFields.dateField2': false,
            'dynamicFields.dateField3': false,
            'dynamicFields.dateField4': false,
            'dynamicFields.dateField5': false,
            'properties.listHeader': false,
            'listMember.text1': false,
            'listMember.text2': false,
            'listMember.date1': false,
            'listMember.date2': false,
            'listMember.num1': false,
            'listMember.num2': false,
            'listMember.check1': false,
            'listMember.check2': false,
            'division.uuid': false,
            'federation.uuid': false,

        } as GridColumnVisibilityModel

        if (!responsive.minMedium) {
            initialVisibilityModel = { ...initialVisibilityModel, 'member.scoutId': false }
            initialVisibilityModel = { ...initialVisibilityModel, 'member.birthdate': false }
            initialVisibilityModel = { ...initialVisibilityModel, 'member.firstname': false }
            initialVisibilityModel = { ...initialVisibilityModel, 'member.lastname': false }
            initialVisibilityModel = { ...initialVisibilityModel, 'member.sex': false }
            initialVisibilityModel = { ...initialVisibilityModel, 'member.name': true }
        } else {
            initialVisibilityModel = { ...initialVisibilityModel, 'member.name': false }
        }

        if (visibility) {
            visibility.map((visibility) => {
                initialVisibilityModel = { ...initialVisibilityModel, [visibility.field]: true }
            });
        }
    }

    const [visibilityModel, setVisibilityModel] = usePersistedVisibilityModel('membership', initialVisibilityModel)
    const [visibilityParams, setVisibiliyParams] = React.useState(copyVisibilityModelToParams(visibilityModel))


    // const settingTableColumn = useSelector(getSettingTableColumn);
    // if (settingTableColumn) {
    //     console.log(settingTableColumn)
    //     initialVisibilityModel = {...initialVisibilityModel, 'member.lastname': settingTableColumn.lastname === true}        
    //     initialVisibilityModel = {...initialVisibilityModel, 'member.firstname': settingTableColumn.firstname === true}
    //     initialVisibilityModel = {...initialVisibilityModel, 'member.birthdate': settingTableColumn.birthdate === true}
    //     initialVisibilityModel = {...initialVisibilityModel, 'properties.section': settingTableColumn.section === true}
    //     initialVisibilityModel = {...initialVisibilityModel, 'properties.joblist': settingTableColumn.jobList === true}        
    // }

    const initialGridPinnedColumnFields = {
        left: ['member.firstname', 'member.lastname', 'member.name'],
    }

    const [pinnedColumns, setPinnedColumns] = usePersistedPinnedColumns('membership', initialGridPinnedColumnFields)
    const handlePinnedColumnsChange = (pinnedColumns: GridPinnedColumnFields) => {
        setPinnedColumns(pinnedColumns)
    }

    const membershipsQuery = useQuery(membershipsQueryOptions(filterParams, sortParams, visibilityParams, paginationModel.page, paginationModel.pageSize))
    const membershipsPage = membershipsQuery.data || { content: [], page: { totalElements: 0, totalPages: 0, number: 0, size: 0 } };
    // console.log(membershipsPage)
    const navigate = useNavigate();

    const handlePaginationModelChange = (model: GridPaginationModel) => {
        console.log('handlePaginationModelChange')
        setPaginationModel(model);
        localStorage.setItem('scoreg.memberships.pageSize', '' + model.pageSize)
        localStorage.setItem('scoreg.memberships.page', '' + model.page)
        // navigate({ from: '/memberships', to: '/memberships', search: (prev: any) => ({ ...prev, page: model.page, size: model.pageSize }) })
    }

    console.log('filterModel')

    const handleFilterModelChange = React.useCallback((model: GridFilterModel) => {
        console.log('handleFilterModelChange')
        // console.log(JSON.stringify(model))

        setFilterModel(model);
        setFilterParams(copyFilterModelToParams(model))
        //navigate({from: '/memberships', to: '/memberships', search: (prev) => ({...prev, filter: filterModel})})

    }, []);

    const handleSortModelChange = React.useCallback((model: GridSortModel) => {
        console.log('handleSortModelChange')
        setSortModel(model);
        setSortParams(copySortModelToParams(model))
    }, [setSortModel]);

    const handleVisibilityModelChange = React.useCallback((model: GridColumnVisibilityModel) => {
        console.log('handleVisibilityModelChange')
        setVisibilityModel(model)
        setVisibiliyParams(copyVisibilityModelToParams(model))
    }, [setVisibilityModel]);

    const [cellModesModel, setCellModesModel] = React.useState<GridCellModesModel>({});

    const handleCellClick = React.useCallback(
        (params: GridCellParams, event: React.MouseEvent) => {

            if (!editable) {
                if (['scoutstate', 'member.scoutId', 'member.lastname', 'member.firstname', 'member.name'].includes(params.colDef.field)) {
                    navigate({
                        from: '/memberships',
                        to: '/member/$memberUuid/main',
                        params: {
                            memberUuid: params.row.member.uuid,
                        }
                    })
                }
            }

            if (!params.isEditable) {
                return;
            }

            // Ignore portal
            if (
                (event.target as any).nodeType === 1 &&
                !event.currentTarget.contains(event.target as Element)
            ) {
                return;
            }

            setCellModesModel((prevModel) => {
                return {
                    // Revert the mode of the other cells from other rows
                    ...Object.keys(prevModel).reduce(
                        (acc, id) => ({
                            ...acc,
                            [id]: Object.keys(prevModel[id]).reduce(
                                (acc2, field) => ({
                                    ...acc2,
                                    [field]: { mode: GridCellModes.View },
                                }),
                                {},
                            ),
                        }),
                        {},
                    ),
                    [params.id]: {
                        // Revert the mode of other cells in the same row
                        ...Object.keys(prevModel[params.id] || {}).reduce(
                            (acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }),
                            {},
                        ),
                        [params.field]: { mode: GridCellModes.Edit },
                    },
                };
            });
        },
        [],
    );

    const handleCellModesModelChange = React.useCallback(
        (newModel: GridCellModesModel) => {
            console.log('handleCellModesModelChange')
            setCellModesModel(newModel);
        },
        [],
    );

    const jobsQuery = useSuspenseQuery(jobsQueryOptions(true));
    const jobs = jobsQuery.data;

    const sectionsQuery = useSuspenseQuery(sectionsAvailableQueryOptions());
    const sections = sectionsQuery.data;

    const organizationsQuery = useSuspenseQuery(organizationsAvailableQueryOptions());
    const organizations = organizationsQuery.data;

    const listHeadersQuery = useQuery(listHeadersQueryOptions(true));
    const listHeaders = listHeadersQuery.data;

    const listHeaderFilter = filterParams.filter((param) => param.field == 'properties.listHeader')
    const listHeader = listHeaderFilter.length > 0 && listHeaders ? listHeaders.filter((listHeader) => listHeader.uuid == listHeaderFilter[0].value) : undefined
    const selectedListHeader = listHeader && listHeader[0]
    useEffect(() => {
        setVisibilityModel({
            ...visibilityModel,
            'properties.listHeader': selectedListHeader ? true : false,
            'listMember.text1': selectedListHeader?.text1 ? true : false,
            'listMember.text2': selectedListHeader?.text2 ? true : false,
            'listMember.date1': selectedListHeader?.date1 ? true : false,
            'listMember.date2': selectedListHeader?.date2 ? true : false,
            'listMember.num1': selectedListHeader?.num1 ? true : false,
            'listMember.num2': selectedListHeader?.num2 ? true : false,
            'listMember.check1': selectedListHeader?.check1 ? true : false,
            'listMember.check2': selectedListHeader?.check2 ? true : false,

        })
    }, [apiRef, selectedListHeader, visibilityModel, setVisibilityModel])

    const permissionsQuery = useSuspenseQuery(fetchDetailPermissionsQueryOptions('member', 'all', ['phone', 'email', 'tableEdit', 'contextMenu']))
    const permissions = permissionsQuery.data

    const contextMenuEnabled =
        permissions &&
        permissions.filter(
            (p) =>
                p.detail === 'contextMenu' && p.permissions.some((a) => a === 'read'),
        ).length > 0

    const organizationDynamicFieldsQuery = useSuspenseQuery(organizationDynamicFieldsQueryOptions(sessionOrganization.uuid))
    const organizationDynamicFields = organizationDynamicFieldsQuery.data;

    const [selectedRow, setSelectedRow] = React.useState<string>();
    const [contextMenu, setContextMenu] = React.useState<{
        mouseX: number;
        mouseY: number;
    } | null>(null);

    const handleContextMenu = (event: React.MouseEvent) => {
        event.preventDefault();
        console.log(event.currentTarget)

        setSelectedRow(event.currentTarget.getAttribute('data-id') || '');
        setContextMenu(
            contextMenu === null
                ? { mouseX: event.clientX - 2, mouseY: event.clientY - 4 }
                : null,
        );

    };

    const handleClose = () => {
        setContextMenu(null);
    };

    const feeClearedTroopMutation = useFeeClearedTroopMutation(() => { }, () => { })

    const handleFeeCleared = () => {
        membershipsPage.content.map((membership: MembershipType) => {
            if (membership.uuid === selectedRow) {
                feeClearedTroopMutation.mutate(membership.uuid || '')
                membership.feeClearedTroop = true
            }
        })
        setContextMenu(null);
    }

    const feeClearedStatus = () => {
        let status = false
        membershipsPage.content.map((membership: MembershipType) => {
            if (membership.uuid === selectedRow) {
                status = membership.feeClearedTroop || false
            }
        })
        return status
    }

    const labelModifier = (label: string) => {
        if (label.includes('listMember.text1')) {
            return selectedListHeader && selectedListHeader.text1 || t('property.listMember.text1');
        } else if (label.includes('listMember.text2')) {
            return selectedListHeader && selectedListHeader.text2 || t('property.listMember.text2');
        } else if (label.includes('listMember.date1')) {
            return selectedListHeader && selectedListHeader.date1 || t('property.listMember.date1');
        } else if (label.includes('listMember.date2')) {
            return selectedListHeader && selectedListHeader.date2 || t('property.listMember.date2');
        } else if (label.includes('listMember.num1')) {
            return selectedListHeader && selectedListHeader.num1 || t('property.listMember.num1');
        } else if (label.includes('listMember.num2')) {
            return selectedListHeader && selectedListHeader.num2 || t('property.listMember.num2');
        } else if (label.includes('listMember.check1')) {
            return selectedListHeader && selectedListHeader.check1 || t('property.listMember.check1');
        } else if (label.includes('listMember.check2')) {
            return selectedListHeader && selectedListHeader.check2 || t('property.listMember.check2');
        }
        return label
    }

    const exportBlob = (blob: Blob, filename: string) => {
        // Save the blob in a json file
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();

        setTimeout(() => {
            URL.revokeObjectURL(url);
        });
    };

    interface EmailExportMenuItemProps extends GridExportMenuItemProps<object> {
        emailScope: string;
    }

    function EmailExportMenuItem(props: EmailExportMenuItemProps) {
        const { hideMenu, emailScope } = props;

        return (
            <MenuItem
                onClick={() => {

                    fetchMembershipEmails(filterParams, emailScope).then((response) => {
                        if (response) {
                            const emailString = response.data.emails.join(';')
                            const blob = new Blob([emailString], {
                                type: 'text/plain;charset=utf-8',
                            });
                            exportBlob(blob, response.data.filename || 'emails' + '.txt')
                        }
                    })

                    // Hide the export menu after the export
                    hideMenu?.();
                    enqueueSnackbar(t('action.downloadRequested'));
                }}
            >
                {t('button.exportEmails.' + emailScope)}
            </MenuItem>
        );
    }

    function EmailExportButton(props: ButtonProps) {
        return (
            <GridToolbarEmailContainer {...props}>
                <EmailExportMenuItem emailScope='MEMBER' />
                <EmailExportMenuItem emailScope='PARENTS' />
                <EmailExportMenuItem emailScope='MEMBER_OR_PARENTS' />
                <EmailExportMenuItem emailScope='MEMBER_AND_PARENTS' />
            </GridToolbarEmailContainer>
        );
    }

    interface ExcelExportMenuItemProps extends GridExportMenuItemProps<object> {
        columnScope: string;
    }

    function ExcelExportMenuItem(props: ExcelExportMenuItemProps) {
        const { hideMenu, columnScope } = props;

        return (
            <MenuItem
                onClick={() => {
                    const visibilityParams = getExportColumns().map((field) => {
                        return {
                            field: field
                        } as VisibilityParam
                    })

                    fetchMembershipExcel(filterParams, sortParams, visibilityParams, columnScope).then((response) => {
                        if (response) {
                            enqueueSnackbar(t('action.downloadCompleted'), { variant: 'success' });
                            const base64 = response.data.base64 || ''
                            // console.log(base64)
                            const raw = Buffer.from(base64, 'base64')
                            // console.log(raw)
                            const blob = new Blob([raw], {
                                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                            });
                            exportBlob(blob, response.data.filename || 'Mitglieder.xlsx')
                        }
                    })

                    // Hide the export menu after the export
                    hideMenu?.();
                    enqueueSnackbar(t('action.downloadRequested'));
                }}
            >
                {t('button.exportExcel.' + columnScope)}
            </MenuItem>
        );
    }


    function ExcelExportButton(props: ButtonProps) {
        return (
            <GridToolbarExcelExportContainer {...props}>
                <ExcelExportMenuItem columnScope='SELECTED' />
                <ExcelExportMenuItem columnScope='ALL' />
            </GridToolbarExcelExportContainer>
        );
    }

    function getExportColumns() {
        const columnDefinitions = gridVisibleColumnDefinitionsSelector(apiRef)

        const visibleColumns = columnDefinitions.map((column) => {
            return column.field
        })

        return visibleColumns
    }

    const handleEditableChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEditable(event.target.checked);
        if (!event.target.checked) {
            queryClient.invalidateQueries({ queryKey: ['memberships'] })
        }
    }

    const CustomToolbar = () => {
        return (
            <Toolbar>
                <GridToolbarColumnsButton slotProps={{
                    button: {
                        style: {
                            minWidth: '20px'
                        }
                    }
                }} />
                <GridToolbarFilterButton slotProps={{
                    badge: {
                        color: 'error',
                    },
                    button: {
                        style: {
                            minWidth: '20px'
                        }
                    }

                }} />
                {responsive.minMedium &&
                    <>

                        <ExcelExportButton />
                        <EmailExportButton />
                        {tableEditable && responsive.minLarge &&
                            <Tooltip title={'Tabelle editierbar'}>
                                <FormControlLabel control={<Switch
                                    checked={editable}
                                    onChange={handleEditableChange}
                                    slotProps={{
                                        input: { 'aria-label': 'controlled' }
                                    }}
                                />
                                } label="Tabelle editierbar" />

                            </Tooltip>
                        }
                    </>
                }

                <ScoregQuickFilter />

                <Box sx={{ flexGrow: 1 }} />

                {(!responsive.xlarge && responsive.minMedium) &&
                    <>
                        <IconButton
                            disabled={membershipsPage.page.number == 0}
                            onClick={() => {
                                setPaginationModel({
                                    ...paginationModel,
                                    page: paginationModel.page - 1
                                })

                            }
                            }>
                            <KeyboardArrowLeftIcon />
                        </IconButton>
                        <IconButton
                            disabled={membershipsPage.page.number >= membershipsPage.page.totalPages}
                            onClick={() => {
                                setPaginationModel({
                                    ...paginationModel,
                                    page: paginationModel.page + 1
                                })
                            }
                            }>
                            <KeyboardArrowRightIcon />
                        </IconButton>
                    </>
                }
                {responsive.xlarge &&
                    <GridPagination />
                }
                {(!responsive.xlarge) &&
                    <GridToolbarPaginationButton />
                }
            </Toolbar>
        )
    }

    const StyledGridOverlay = styled('div')(({ theme }) => ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        '& .no-rows-primary': {
            fill: '#3D4751',
            ...theme.applyStyles('light', {
                fill: '#AEB8C2',
            }),
        },
        '& .no-rows-secondary': {
            fill: '#1D2126',
            ...theme.applyStyles('light', {
                fill: '#E8EAED',
            }),
        },
    }));

    const CustomNoRowsOverlay = () => {
        return (
            <StyledGridOverlay>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    width={96}
                    viewBox="0 0 452 257"
                    aria-hidden
                    focusable="false"
                >
                    <path
                        className="no-rows-primary"
                        d="M348 69c-46.392 0-84 37.608-84 84s37.608 84 84 84 84-37.608 84-84-37.608-84-84-84Zm-104 84c0-57.438 46.562-104 104-104s104 46.562 104 104-46.562 104-104 104-104-46.562-104-104Z"
                    />
                    <path
                        className="no-rows-primary"
                        d="M308.929 113.929c3.905-3.905 10.237-3.905 14.142 0l63.64 63.64c3.905 3.905 3.905 10.236 0 14.142-3.906 3.905-10.237 3.905-14.142 0l-63.64-63.64c-3.905-3.905-3.905-10.237 0-14.142Z"
                    />
                    <path
                        className="no-rows-primary"
                        d="M308.929 191.711c-3.905-3.906-3.905-10.237 0-14.142l63.64-63.64c3.905-3.905 10.236-3.905 14.142 0 3.905 3.905 3.905 10.237 0 14.142l-63.64 63.64c-3.905 3.905-10.237 3.905-14.142 0Z"
                    />
                    <path
                        className="no-rows-secondary"
                        d="M0 10C0 4.477 4.477 0 10 0h380c5.523 0 10 4.477 10 10s-4.477 10-10 10H10C4.477 20 0 15.523 0 10ZM0 59c0-5.523 4.477-10 10-10h231c5.523 0 10 4.477 10 10s-4.477 10-10 10H10C4.477 69 0 64.523 0 59ZM0 106c0-5.523 4.477-10 10-10h203c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10ZM0 153c0-5.523 4.477-10 10-10h195.5c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10ZM0 200c0-5.523 4.477-10 10-10h203c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10ZM0 247c0-5.523 4.477-10 10-10h231c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10Z"
                    />
                </svg>
                <Box sx={{ mt: 2 }}>Keine Ergebnisse - {filterModel?.items.length} Filter aktiv</Box>
            </StyledGridOverlay>
        )
    }

    const defaultLocaleText = deDE.components.MuiDataGrid.defaultProps.localeText

    const localeText = responsive.xlarge ? defaultLocaleText : { ...defaultLocaleText, ...{ toolbarColumns: '', toolbarFilters: '' } }

    const allColumns = getColumns(responsive, jobs, sections, organizations, permissions, [], organizationDynamicFields, [], listHeaders, labelModifier, editable)

    const columnsState = useColumnState('membership', apiRef, allColumns);

    const getTogglableColumns = (columns: GridColDef[]) => {

        if (selectedListHeader) {
            return columns.map((column) => column.field)
        } else {
            return columns
                .filter((column) => !column.field.startsWith('listMember.'))
                .map((column) => column.field)
        }

    };

    const tableEditable =
        permissions &&
        permissions.filter(
            (p) =>
                p.detail === 'tableEdit' && p.permissions.some((a) => a === 'write'),
        ).length > 0

    if (membershipsQuery.isLoading || membershipsQuery.isFetching) {
        return <LoadProgress />
    }

    if (permissionsQuery.isLoading || permissionsQuery.isFetching) {
        return <LoadProgress />
    }

    if (organizationDynamicFieldsQuery.isLoading || organizationDynamicFieldsQuery.isFetching) {
        return <LoadProgress />
    }

    if (listHeadersQuery.isLoading || listHeadersQuery.isFetching) {
        return <LoadProgress />
    }

    if (apiRef == null) {
        return <LoadProgress />
    }

    return (
        <Box sx={{
            height: { xs: `calc(100% - 50px)` }
        }}>
            <Box sx={{
                alignContent: 'left'
            }}>
                <MembershipsSidebar apiRef={apiRef} filterModel={filterModel} />
            </Box>
            <Box
                sx={{
                    width: { xs: '100%', md: `calc(100% - 100px)` },
                    height: { xs: '100%' }
                }}>
                <div style={{ display: 'flex', flexDirection: 'column', maxHeight: 'calc(100% - 1px)', minHeight: '200px' }}>
                    <DataGridPremium
                        apiRef={apiRef}
                        columns={columnsState.columns}
                        onColumnWidthChange={columnsState.onColumnWidthChange}
                        onColumnOrderChange={columnsState.onColumnOrderChange}
                        rows={membershipsPage.content}
                        getRowId={(row) => { return row && row.uuid || '' }}
                        rowCount={membershipsPage.page.totalElements}
                        rowHeight={40}
                        pagination
                        paginationMode='server'
                        filterDebounceMs={500}
                        paginationModel={paginationModel}
                        pageSizeOptions={pageSizeOptions}
                        onPaginationModelChange={handlePaginationModelChange}
                        filterMode='server'
                        loading={membershipsQuery.isLoading || membershipsQuery.isFetching}
                        filterModel={filterModel}
                        onFilterModelChange={handleFilterModelChange}
                        sortingMode='server'
                        sortModel={sortModel}
                        onSortModelChange={handleSortModelChange}
                        columnVisibilityModel={visibilityModel}
                        onColumnVisibilityModelChange={handleVisibilityModelChange}
                        pinnedColumns={pinnedColumns}
                        onPinnedColumnsChange={handlePinnedColumnsChange}
                        // onColumnOrderChange={handleColumnOrderChange}
                        getCellClassName={(params: GridCellParams<any, any, number>) => {
                            return params.row.active ? 'scoreg-active' : 'scoreg-inactive'
                        }}
                        slots={{
                            toolbar: CustomToolbar,
                            noRowsOverlay: CustomNoRowsOverlay
                        }}
                        localeText={localeText}
                        autosizeOptions={{
                            columns: ['properties.joblist'],
                            includeHeaders: true,
                            includeOutliers: false,
                        }}
                        disableMultipleRowSelection={true}
                        disableRowGrouping={true}
                        disableAggregation={true}
                        cellModesModel={cellModesModel}
                        hideFooter={true}
                        onCellModesModelChange={handleCellModesModelChange}
                        onCellClick={handleCellClick}
                        isCellEditable={(params) => { return editable && tableEditable && params.colDef.editable == true }}
                        processRowUpdate={(updatedRow, originalRow) => {
                            enqueueSnackbar(t('action.record.changed'), { variant: 'success' });
                            return updateMembershipTable([updatedRow, originalRow])
                        }}
                        onProcessRowUpdateError={(error) => {
                            console.log(error)
                        }}
                        // initialState={{
                        //     pagination: {
                        //         paginationModel: paginationModel,
                        //     },
                        // }}
                        sx={{
                            '& .MuiDataGrid-cell': {
                                paddingLeft: 0.5,
                                paddingRight: 0.5,
                            },
                            '--DataGrid-overlayHeight': '300px'
                        }}
                        slotProps={{
                            filterPanel: {
                                logicOperators: [GridLogicOperator.And],
                                filterFormProps: {
                                    logicOperatorInputProps: {
                                        sx: { display: 'none' }
                                    }
                                },
                                sx: { maxWidth: '95vw' }
                            },
                            row: {
                                onContextMenu: contextMenuEnabled ? handleContextMenu : undefined,
                                style: { cursor: 'context-menu' }
                            },
                            columnsManagement: {
                                disableShowHideToggle: responsive.xsmall || responsive.small,
                                disableResetButton: responsive.xsmall || responsive.small,
                                getTogglableColumns: getTogglableColumns,
                            },
                        }}
                        showToolbar />
                </div>
                <Menu
                    open={contextMenu !== null}
                    onClose={handleClose}
                    anchorReference='anchorPosition'
                    anchorPosition={
                        contextMenu !== null
                            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                            : undefined
                    }
                    slotProps={{
                        root: {
                            onContextMenu: (event: { preventDefault: () => void; }) => {
                                event.preventDefault();
                                handleClose();
                            },
                        },
                    }}
                >
                    {
                        feeClearedStatus() === false && <MenuItem onClick={handleFeeCleared}>Beitrag bezahlt</MenuItem>
                    }

                </Menu>
            </Box>
        </Box>
    );
}
